import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Form, Tabs, Tab } from 'react-bootstrap';
import { get } from 'lodash';
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import moment from 'moment';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import CustomImage from '../../components/CustomImage';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import LeadHistory from './LeadHistory';
import LeadFile from './LeadFile';
import LeadIvrOpenNewCanceled from './LeadIvrOpenNewCanceled';
import LeadNotification from './LeadNotification';

export default function Lead() {
  const [activeTab, setActiveTab] = useState('main');
  const navigate = useNavigate();
  const routeName = '/leads';
  const dataName = 'Lead';
  const titleName = 'Lead';
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const params = useParams();
  const id = get(params, 'id', '');
  const [companies, setCompanies] = useState([]);
  const [channels, setChannels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [formValues, setFormValues] = useState({
    name: '',
    notes: '',
    doc_id: '',
    user_id: null,
  });
  const [leadUpdated, setLeadUpdated] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    company_id: Yup.number().required('Empresa obrigatória.'),
    doc_id: Yup.number().min(
      6,
      'Documento deve conter no mínimo 6 caracteres.'
    ),
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: fieldName === 'company_id' ? Number(value) : value,
    }));
  }, []);

  const fields = [
    {
      name: 'name',
      id: 'name',
      label: 'Nome',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'company_id',
      id: 'company_id',
      label: 'Empresa',
      type: 'select',
      options: companies,
      onChange: (e) => handleFieldChange('company_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'doc_id',
      id: 'doc_id',
      label: 'Documento',
      type: 'text',
      onChange: (e) => handleFieldChange('doc_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'channel_id',
      id: 'channel_id',
      label: 'Fonte do lead',
      type: 'select',
      options: channels,
      onChange: (e) => handleFieldChange('channel_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'customer_id',
      id: 'customer_id',
      label: 'Cliente',
      type: 'select',
      options: customers,
      onChange: (e) => handleFieldChange('customer_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'cassification_id',
      id: 'cassification_id',
      label: 'Classificação do lead',
      type: 'select',
      options: classifications,
      onChange: (e) => handleFieldChange('cassification_id', e.target.value),
      colSize: 4,
      additionalProps: {},
    },
    {
      name: 'user_id',
      id: 'user_id',
      label: 'Atribuir usuário',
      type: 'select',
      options: users,
      onChange: (e) => handleFieldChange('user_id', e.target.value),
      colSize: 12,
      additionalProps: {},
    },
    {
      name: 'notes',
      id: 'notes',
      label: 'Observações',
      type: 'textarea',
      onChange: (e) => handleFieldChange('notes', e.target.value),
      colSize: 12,
      additionalProps: {},
    },
  ];

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      let action = 'cadastrado';

      if (id) {
        await axios.put(`${routeName}/${id}`, formValues);
        action = 'atualizado';
      } else {
        await axios.post(routeName, formValues);
      }

      if (!id) {
        navigate(routeName);
      }

      notification(`${dataName} ${action} com sucesso`, 'success');
      setLeadUpdated(true);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const alterStatusLead = useCallback(async () => {
    try {
      setIsLoading(true);
      handleFieldChange('closed', formValues.closed === 0 ? 1 : 0);
      const lead = await axios.put(
        `${routeName}/${id}/tooglestatus`,
        formValues
      );
      notification(
        `Lead ${lead.data.closed === 0 ? 'reaberto' : 'encerrado'} com sucesso`,
        'success'
      );
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, [formValues]);

  const getLead = useCallback(async () => {
    try {
      const request = await axios.get(`${routeName}/${id}`);
      setFormValues({
        ...request.data,
      });
    } catch (err) {
      setApiErrors(err);
    }
  }, [id]);

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);
      const optionsCompanies = [{ value: '', label: '-- Selecione --' }];
      const optionsChannels = [{ value: '', label: '-- Selecione --' }];
      const optionsClassifiers = [{ value: '', label: 'Não classificado' }];
      const optionsCustomers = [{ value: '', label: '-- Selecione --' }];
      const optionsUsers = [{ value: '', label: '-- Selecione --' }];

      const getCompanies = await axios.get(`/companies`);
      if (getCompanies) {
        getCompanies.data.forEach((data) => {
          optionsCompanies.push({
            value: data.id,
            label: data.name,
          });
        });
      }
      setCompanies(optionsCompanies);

      const getChannels = await axios.get(`/channels`);
      if (getChannels) {
        getChannels.data.forEach((data) => {
          if (data.company_id === formValues.company_id) {
            optionsChannels.push({
              value: data.id,
              label: data.name,
            });
          }
        });
      }
      setChannels(optionsChannels);

      const getClassifiers = await axios.get(`/classifiers`);
      if (getClassifiers) {
        getClassifiers.data.forEach((data) => {
          if (data.company_id === formValues.company_id) {
            optionsClassifiers.push({
              value: data.id,
              label: data.name,
            });
          }
        });
      }
      setClassifications(optionsClassifiers);

      const getCustomers = await axios.get(`/customers`);
      if (getCustomers) {
        getCustomers.data.forEach((data) => {
          if (data.company_id === formValues.company_id) {
            optionsCustomers.push({
              value: data.id,
              label: data.name,
            });
          }
        });
      }
      setCustomers(optionsCustomers);

      const getUsers = await axios.get(`/users`);
      if (getUsers) {
        getUsers.data.forEach((data) => {
          if (
            data.isadmin === 1 ||
            data.Companies.some(
              (company) => company.id === formValues.company_id
            )
          ) {
            optionsUsers.push({
              value: data.id,
              label: data.UserProfile.name,
            });
          }
        });
      }
      setUsers(optionsUsers);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
      setDataLoaded(true);
    }
  }, [formValues]);

  useEffect(() => {
    if (id) {
      getLead();
    }
    getData();
  }, [id]);

  useEffect(() => {
    getData();
  }, [formValues.company_id]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: routeName, name: 'Leads' }]}
      />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">{titleName}</h1>
        {formValues.id && formValues.user_id && dataLoaded ? (
          <div>
            <ButtonModal
              onConfirm={() => alterStatusLead()}
              message={`Confirma ${formValues.closed === 0 ? 'encerramento' : 'reabertura'} do lead ${formValues.name} ?`}
              buttonProps={{
                variant: formValues.closed === 0 ? 'danger' : 'success',
                className: 'text-uppercase text-white',
                title: `Clique para ${formValues.closed === 0 ? 'fechar' : 'reabrir'} o lead`,
              }}
              textModal={{
                title: `${formValues.closed === 0 ? 'Fechar' : 'Reabrir'} lead`,
                textConfirm: `Confirmar`,
              }}
            >
              {formValues.closed === 0 ? 'Fechar' : 'Reabrir'}
            </ButtonModal>
          </div>
        ) : null}
      </div>
      <section>
        <ErrorProcessor error={apiErrors} />
        <Tabs
          activeKey={activeTab}
          onSelect={(eventKey) => setActiveTab(eventKey)}
          className="mb-5"
        >
          <Tab eventKey="main" title="Cadastro">
            <Row>
              {id && dataLoaded ? (
                <Col lg={4}>
                  <>
                    <Card className="card-profile mb-4">
                      <Card.Header
                        className="overflow-hidden"
                        style={{
                          backgroundImage: `url(/img/photos/greenShapes.jpg)`,
                        }}
                      />
                      <Card.Body className="text-center">
                        <div
                          className="card-profile-img mx-auto d-flex align-center"
                          style={{ maxHeight: '8rem' }}
                        >
                          <div className="position-relative overflow-hidden rounded-circle">
                            <CustomImage
                              src={formValues.photo || '/img/phone-circle.png'}
                              layout="fixed"
                              width={122}
                              height={122}
                              priority
                            />
                          </div>
                        </div>

                        <h3 className="mb-3">{formValues.name}</h3>
                        <p className="mb-4">{formValues.doc_id}</p>
                      </Card.Body>
                    </Card>
                    <Card className="mb-4">
                      <Card.Header>
                        <h4 className="card-heading">Informações do Lead</h4>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col>
                            <Form.Label>Status</Form.Label>
                            <h6
                              className={
                                formValues.closed === 0
                                  ? 'text-success'
                                  : 'text-danger'
                              }
                            >
                              {formValues.closed === 0 ? 'Aberto' : 'Fechado'}
                            </h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                        <Row>
                          <Col>
                            <Form.Label>Empresa</Form.Label>
                            <h6>{formValues.Company.name}</h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                        <Row>
                          <Col>
                            <Form.Label>Fonte de lead</Form.Label>
                            <h6>{formValues.Channel.name}</h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                        <Row>
                          <Col>
                            <Form.Label>Classificação</Form.Label>
                            <h6>
                              {formValues.Classifier
                                ? formValues.Classifier.name
                                : 'Não classificado'}
                            </h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                        <Row>
                          <Col>
                            <Form.Label>Data de inclusão</Form.Label>
                            <h6>
                              {moment(formValues.created_at).format(
                                'DD/MM/YYYY'
                              )}
                            </h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                        <Row>
                          <Col>
                            <Form.Label>Protocolo</Form.Label>
                            <h6>{formValues.protocol}</h6>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </>
                </Col>
              ) : null}
              <Col lg={id ? 8 : 12}>
                {!id || dataLoaded ? (
                  <Card className="mb-5">
                    <Card.Header>
                      <h4 className="card-heading">
                        {id
                          ? `Informações de cadastro`
                          : `Cadastrar ${dataName}`}
                      </h4>
                    </Card.Header>
                    <Card.Body>
                      <Forms
                        initialValues={formValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        fields={fields}
                        btnSubmit={{
                          variant: 'primary',
                          type: 'submit',
                          text: id ? 'Atualizar' : 'Cadastrar',
                        }}
                      />
                    </Card.Body>
                  </Card>
                ) : null}
                {formValues.LeadIvrOpenNewCanceled && (
                  <LeadIvrOpenNewCanceled lead={formValues} />
                )}
              </Col>
            </Row>
          </Tab>
          {id && dataLoaded && (
            <Tab eventKey="file" title="Arquivos">
              <Row>
                {id && dataLoaded && (
                  <LeadFile
                    lead={formValues}
                    stateAction={setLeadUpdated}
                    stateInfo={leadUpdated}
                  />
                )}
              </Row>
            </Tab>
          )}
          {id && dataLoaded && (
            <Tab eventKey="history" title="Histórico">
              <LeadHistory
                lead={formValues}
                stateAction={setLeadUpdated}
                stateInfo={leadUpdated}
              />
            </Tab>
          )}
          {id && dataLoaded && (
            <Tab eventKey="notification" title="Enviar mensagem">
              <LeadNotification
                lead={formValues}
                stateAction={setLeadUpdated}
                stateInfo={leadUpdated}
              />
            </Tab>
          )}
        </Tabs>
      </section>
    </Container>
  );
}
